import type { BackupRun, BackupSummary } from '../../../types/models/Backup';

/**
 * Pure logic behind the backups tree table — filtering, flattening and the small display
 * derivations that go with them. Kept dependency-free and free of JSX so it can be unit-tested
 * on its own (see `backupTree.test.ts`); `BackupTreeTable.tsx` only maps these rows to markup.
 *
 * The tree is two levels deep:
 *
 * - **level 1** — one *subject*: a `<backup target>/<customer>/<hypervisor>/<vm>` directory.
 *   `vm_name` is **not** unique, so the same VM backed up from two hypervisors (or mirrored to two
 *   targets) legitimately produces two level-1 rows. They are not merged: they describe different
 *   directories.
 * - **level 2** — that subject's *restore points*, newest first, loaded lazily on first expand.
 *
 * Collapsed children are **omitted from the row array** rather than emitted with `isHidden`.
 * PatternFly's `isHidden` only marks the `<tr>`; a hidden row still occupies a pagination slot, so
 * paginating over hidden rows yields short-looking pages.
 */

/** How a subject's restore points are doing, from the caller's cache. */
export interface SubjectRunsState {
  runs: BackupRun[];
  /** Total restore points for this subject, ignoring how many were loaded. */
  count: number;
}

/**
 * A flattened backup tree-table row.
 *
 * `childCount` feeds `aria-setsize` — PatternFly renders the expand chevron only when it is > 0,
 * so leaves must pass `0`. `posInSet` feeds `aria-posinset`, `level` feeds `aria-level`.
 *
 * Besides real run rows, a subject can emit three synthetic children so that a slow or failed
 * lazy load degrades inside the tree instead of taking down the whole table.
 */
export type BackupTreeRow =
  | {
      kind: 'subject';
      key: string;
      subject: BackupSummary;
      level: 1;
      posInSet: number;
      childCount: number;
      expanded: boolean;
    }
  | { kind: 'run'; key: string; subjectId: number; run: BackupRun; level: 2; posInSet: number }
  | { kind: 'runs-loading'; key: string; subjectId: number; level: 2; posInSet: number }
  | { kind: 'runs-error'; key: string; subjectId: number; message: string; level: 2; posInSet: number }
  | { kind: 'runs-more'; key: string; subjectId: number; remaining: number; level: 2; posInSet: number };

/**
 * The status filter's options. `retired` and `missing` are **orthogonal** subject states, not
 * points on one scale — a deleted `.old` tree is both, and shows under either option.
 */
export type BackupStatusFilter = 'all' | 'active' | 'retired' | 'missing';

export const subjectKey = (subjectId: number): string => `subject:${subjectId}`;
export const runKey = (subjectId: number, runId: number): string => `run:${subjectId}:${runId}`;

/** A subject is "active" when it is present on the backup server and was not decommissioned. */
export const isSubjectActive = (subject: BackupSummary): boolean =>
  !subject.retired && subject.missing_since === null;

/**
 * Whether a subject counts as stale, given the threshold the backend published in
 * `summary.stale_after_hours`.
 *
 * Deliberately mirrors the backend's own `stale_count` definition so a row badge can never
 * disagree with the summary card: present, non-retired subjects whose last backup is missing
 * entirely or older than the threshold. Returns `false` when no threshold was supplied — a
 * surface without the summary call (the VM tab) shows the factual age and passes no verdict,
 * which is better than hardcoding the number.
 */
export const isSubjectStale = (subject: BackupSummary, staleAfterHours: number | null): boolean => {
  if (staleAfterHours === null || subject.retired || subject.missing_since !== null) {
    return false;
  }
  if (subject.hours_since_last_backup === null) {
    return true;
  }
  // `>=`, not `>`: the backend **floors** `hours_since_last_backup` to whole hours while its
  // `stale_count` compares against an exact instant. So a subject last backed up 48 h 30 m ago
  // reports `48` and is already counted as stale server-side; a strict `>` would badge no row
  // until the counter ticked to 49, leaving the Attention card pointing at a VM the user cannot
  // find for up to an hour.
  return subject.hours_since_last_backup >= staleAfterHours;
};

/** Whether a subject passes the status filter. */
export const subjectMatchesStatus = (subject: BackupSummary, status: BackupStatusFilter): boolean => {
  switch (status) {
    case 'active':
      return isSubjectActive(subject);
    case 'retired':
      return subject.retired;
    case 'missing':
      return subject.missing_since !== null;
    case 'all':
    default:
      return true;
  }
};

/**
 * Whether a subject matches the search box, which searches the **VM name** only.
 *
 * Case-insensitive and literal — unlike the backend's `vm_name` parameter, which is a raw
 * `LIKE '%term%'`: case-sensitive, with `%` and `_` unescaped (so `db_prod` would silently match
 * `db-prod`). Filtering here gives the behaviour a human typing a name expects.
 */
export const subjectMatchesQuery = (subject: BackupSummary, query: string): boolean => {
  const q = query.trim().toLowerCase();
  if (q === '') {
    return true;
  }
  return subject.vm_name.toLowerCase().includes(q);
};

/**
 * Apply the search box and the status filter. Returns the same array reference when neither
 * filter is active, so callers can rely on referential equality to skip work.
 */
export const filterBackups = (
  subjects: BackupSummary[],
  query: string,
  status: BackupStatusFilter,
): BackupSummary[] => {
  if (query.trim() === '' && status === 'all') {
    return subjects;
  }
  return subjects.filter((s) => subjectMatchesStatus(s, status) && subjectMatchesQuery(s, query));
};

export interface FlattenBackupsOptions {
  /** Whether a subject's restore points are currently revealed. */
  isExpanded: (subjectId: number) => boolean;
  /** The subject's loaded restore points, or `undefined` if they were never fetched. */
  runsFor: (subjectId: number) => SubjectRunsState | undefined;
  /** Whether a fetch for that subject's restore points is in flight. */
  isLoadingRuns: (subjectId: number) => boolean;
  /** The message from the last failed fetch for that subject, if any. */
  runsError: (subjectId: number) => string | undefined;
}

/**
 * Flatten the subjects into the ordered row list the tree table renders.
 *
 * Subject rows always appear; a subject's children appear only while it is expanded. The subject
 * order is the server's (`last_backup_at DESC, vm_name ASC, hypervisor ASC`) and is never
 * re-sorted here; runs keep the server's `run_at DESC`.
 */
export const flattenBackupTree = (
  subjects: BackupSummary[],
  options: FlattenBackupsOptions,
): BackupTreeRow[] => {
  const { isExpanded, runsFor, isLoadingRuns, runsError } = options;
  const rows: BackupTreeRow[] = [];

  subjects.forEach((subject, index) => {
    const expanded = isExpanded(subject.id);
    rows.push({
      kind: 'subject',
      key: subjectKey(subject.id),
      subject,
      level: 1,
      posInSet: index + 1,
      // The chevron renders only when this is > 0, so a subject with no restore points on disk
      // correctly gets none. `backup_count` is known up front, before the children are fetched.
      childCount: subject.backup_count,
      expanded,
    });

    if (!expanded) {
      return;
    }

    const loaded = runsFor(subject.id);
    const message = runsError(subject.id);
    const hasRows = loaded !== undefined && loaded.runs.length > 0;

    // Cached rows win over both spinner and error: a refetch (or a failed retry) must not blank
    // out restore points the user can already see. Only when there is nothing to show do the
    // placeholder children appear — and they replace this subject's children only, never the
    // whole table.
    if (!hasRows && isLoadingRuns(subject.id)) {
      rows.push({
        kind: 'runs-loading',
        key: `runs-loading:${subject.id}`,
        subjectId: subject.id,
        level: 2,
        posInSet: 1,
      });
      return;
    }

    if (!hasRows && message !== undefined) {
      rows.push({
        kind: 'runs-error',
        key: `runs-error:${subject.id}`,
        subjectId: subject.id,
        message,
        level: 2,
        posInSet: 1,
      });
      return;
    }

    if (loaded === undefined) {
      // Expanded but neither loading nor loaded — the caller kicks the fetch off on expand, so
      // this is the single frame before that lands.
      rows.push({
        kind: 'runs-loading',
        key: `runs-loading:${subject.id}`,
        subjectId: subject.id,
        level: 2,
        posInSet: 1,
      });
      return;
    }

    loaded.runs.forEach((run, runIndex) => {
      rows.push({
        kind: 'run',
        key: runKey(subject.id, run.id),
        subjectId: subject.id,
        run,
        level: 2,
        posInSet: runIndex + 1,
      });
    });

    const remaining = loaded.count - loaded.runs.length;
    if (remaining > 0) {
      rows.push({
        kind: 'runs-more',
        key: `runs-more:${subject.id}`,
        subjectId: subject.id,
        remaining,
        level: 2,
        posInSet: loaded.runs.length + 1,
      });
    }
  });

  return rows;
};

/**
 * Purely a **display** boundary: below this, an age reads better in hours than in days.
 *
 * Nothing to do with staleness — that threshold is the backend's alone, arrives in
 * `summary.stale_after_hours`, and is never written as a literal in this codebase.
 */
const HOURS_BEFORE_SWITCHING_TO_DAYS = 72;

/**
 * Render the server's `hours_since_last_backup` as a short relative age: `"just now"`, `"5h ago"`,
 * `"3 days ago"`, `"4 months ago"`.
 *
 * Always derived from that server-computed integer, never from the timestamps — the timestamps
 * carry no timezone offset, so measuring them against the browser clock would reintroduce exactly
 * the shift the wall-clock formatter exists to avoid. Returns `null` when the subject has never
 * run, so callers can omit the hint entirely.
 */
export const formatBackupAge = (hoursSinceLastBackup: number | null): string | null => {
  if (hoursSinceLastBackup === null) {
    return null;
  }
  if (hoursSinceLastBackup < 1) {
    return 'just now';
  }
  if (hoursSinceLastBackup < HOURS_BEFORE_SWITCHING_TO_DAYS) {
    return `${hoursSinceLastBackup}h ago`;
  }
  const days = Math.floor(hoursSinceLastBackup / 24);
  if (days < 60) {
    return `${days} days ago`;
  }
  const months = Math.floor(days / 30);
  if (months < 24) {
    return `${months} months ago`;
  }
  return `${Math.floor(days / 365)} years ago`;
};

/**
 * Render a run's `duration_seconds` compactly: `"4s"`, `"2m 05s"`, `"1h 12m"`.
 *
 * Returns `'—'` for `null`, which is the normal case for runs made before the backup process
 * stamped that metadata — `started_at` / `ended_at` / `duration_seconds` are a null triple, and a
 * duration must never be inferred from `run_at`.
 */
export const formatRunDuration = (durationSeconds: number | null): string => {
  if (durationSeconds === null || !Number.isFinite(durationSeconds) || durationSeconds < 0) {
    return '—';
  }
  if (durationSeconds < 60) {
    return `${durationSeconds}s`;
  }
  const minutes = Math.floor(durationSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m ${String(durationSeconds % 60).padStart(2, '0')}s`;
  }
  return `${Math.floor(minutes / 60)}h ${String(minutes % 60).padStart(2, '0')}m`;
};
